'use client'

import React, { ReactNode, useState } from 'react'

import { HTMLChakraProps, chakra } from '@chakra-ui/react'

import { InfoIcon } from '../Icon/InfoIcon'
import { Tooltip, TooltipVariant } from '../Tooltip'
import { Pill } from './Pill'
import {
  ColorScheme,
  StatusPillVariant,
  VARIANT_TOKENS,
  resolveTokens,
} from './StatusPill.styles'

export type { ColorScheme, StatusPillVariant }

export type StatusPillProps = Omit<HTMLChakraProps<'span'>, 'color'> & {
  icon?: ReactNode
  label?: string
  variant: StatusPillVariant
  tooltip?: string
  /** Style of the integrated tooltip: 'label' (uppercase, single line) or
   * 'description' (multiline body copy). Defaults to 'label'. */
  tooltipVariant?: TooltipVariant
  colorScheme?: ColorScheme
}

export const StatusPill = ({
  icon,
  label,
  variant,
  tooltip,
  tooltipVariant,
  colorScheme = 'dark',
  ...rest
}: StatusPillProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null)
  const tokens = resolveTokens(
    VARIANT_TOKENS[variant] ?? VARIANT_TOKENS['teal'],
    colorScheme
  )

  return (
    <Pill
      bg={tokens.bg}
      border={tokens.border ? `1.5px solid ${tokens.border}` : undefined}
      color={tokens.color}
      {...rest}
    >
      {tokens.dotColor && (
        <chakra.span
          bg={tokens.dotColor}
          borderRadius='full'
          display='inline-block'
          flexShrink={0}
          h='6px'
          w='6px'
        />
      )}
      {icon && (
        <chakra.span alignItems='center' display='inline-flex' flexShrink={0}>
          {icon}
        </chakra.span>
      )}
      {label}
      {tooltip && (
        <chakra.span
          alignItems='center'
          cursor='default'
          display='inline-flex'
          flexShrink={0}
          onMouseEnter={(e) => setAnchorEl(e.currentTarget as HTMLSpanElement)}
          onMouseLeave={() => setAnchorEl(null)}
        >
          <InfoIcon colorScheme={colorScheme} />
          <Tooltip
            anchorEl={anchorEl}
            colorScheme={colorScheme}
            label={tooltip}
            variant={tooltipVariant}
          />
        </chakra.span>
      )}
    </Pill>
  )
}
